import { computed, ref, Ref, watch } from "vue";
import { Branch, useExcludedBranch } from "./useExcludedBranch";
import {
	branchKey,
	branchOfLabelAndLevel,
	childrenOfBranch,
	isWithin,
	nodeAtBranch,
	pathFromRoot,
} from "./hierarchyTree";
import { buildDepthOptions, clampDepth } from "./hierarchyDepth";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;

export const useHierarchyTree = (root: Ref<QuickFilterNodeDTO | null>) => {
	const {
		excludedBranches,
		toggleBranch,
		setBranch,
		isBranchExcluded,
		isBranchIndeterminate,
		clearBranches,
	} = useExcludedBranch();

	const scopeBranch = ref<Branch>([]);

	const expanded = ref<Set<string>>(new Set());
	const depth = ref(1);

	const scope = computed(
		() => nodeAtBranch(root.value, scopeBranch.value) ?? root.value
	);

	const breadcrumb = computed(() =>
		pathFromRoot(root.value, scopeBranch.value)
	);

	const canGoUp = computed(() => scopeBranch.value.length > 0);

	const depthOptions = computed(() =>
		scope.value
			? buildDepthOptions(scope.value, {
					isExcluded: isBranchExcluded,
					scopeBranch: scopeBranch.value,
			  })
			: []
	);

	const depthYield = computed(
		() =>
			depthOptions.value.find((option) => option.depth === depth.value)
				?.count ?? null
	);

	const excludedNodes = computed(() =>
		excludedBranches.value
			.map((branch) => nodeAtBranch(root.value, branch))
			.filter((node): node is QuickFilterNodeDTO => Boolean(node))
			.map((node) => ({ level: node.level, label: node.label }))
	);

	const exclusions = computed(() =>
		excludedBranches.value
			.map((branch) => ({ branch, path: pathFromRoot(root.value, branch) }))
			.filter((entry) => entry.path.length > 1)
			.map((entry) => ({
				branch: entry.branch,
				label: entry.path[entry.path.length - 1].visibleLabel,
				parents: entry.path.slice(1, -1).map((node) => node.visibleLabel),
			}))
	);

	const removeExclusion = (branch: Branch) => setBranch(branch, false, true);

	const isBranchPruned = (branch: Branch): boolean =>
		branch.some((_, depth) => isBranchExcluded(branch.slice(0, depth + 1)));

	const includedChildren = (parent: Branch) =>
		childrenOfBranch(root.value, parent).filter(
			(child) => !isBranchExcluded(child.branch)
		);

	const canExclude = (branch: Branch): boolean => {
		if (isBranchExcluded(branch) || isBranchIndeterminate(branch)) {
			return true;
		}

		return includedChildren(branch.slice(0, -1)).length > 1;
	};

	const setExcluded = (branch: Branch, excluded: boolean) => {
		if (excluded && !canExclude(branch)) {
			return;
		}

		setBranch(branch, excluded, true);
	};

	const toggleExclusion = (branch: Branch) => {
		if (isBranchIndeterminate(branch)) {
			setBranch(branch, false, true);
			return;
		}

		if (!canExclude(branch)) {
			return;
		}

		toggleBranch(branch);
	};

	const isolate = (keep: Branch[], level: Branch[]) => {
		level.forEach((branch) => setBranch(branch, true));
		keep.forEach((branch) => setBranch(branch, false, true));
	};

	const includeAll = (level: Branch[]) => {
		level.forEach((branch) => setBranch(branch, false, true));
	};

	const isExpanded = (branch: Branch) => expanded.value.has(branchKey(branch));

	const toggleExpanded = (branch: Branch) => {
		const key = branchKey(branch);
		const next = new Set(expanded.value);

		if (next.has(key)) {
			next.delete(key);
		} else {
			next.add(key);
		}

		expanded.value = next;
	};

	const collapseAll = () => {
		expanded.value = new Set();
	};

	const expandPath = (branch: Branch = scopeBranch.value) => {
		const keys = new Set<string>([branchKey([])]);

		for (let length = 1; length < branch.length; length++) {
			keys.add(branchKey(branch.slice(0, length)));
		}

		expanded.value = keys;
	};

	const setScope = (branch: Branch) => {
		if (isBranchPruned(branch)) {
			return;
		}

		scopeBranch.value = [...branch];

		depth.value = clampDepth(scope.value, depth.value);
		expandPath();
	};

	const goUp = () => {
		if (!canGoUp.value) {
			return;
		}

		setScope(scopeBranch.value.slice(0, -1));
	};

	const setDepth = (value: number) => {
		depth.value = clampDepth(scope.value, value);
	};

	const loadFromValue = (value: FilterValueDTO | null | undefined) => {
		clearBranches();
		collapseAll();

		if (!value) {
			scopeBranch.value = [];
			depth.value = 1;
			return;
		}

		scopeBranch.value =
			branchOfLabelAndLevel(root.value, value.level, value.label) ?? [];

		(value.excluded_branches ?? []).forEach((excluded) => {
			const branch = branchOfLabelAndLevel(
				root.value,
				excluded.level,
				excluded.label
			);

			if (branch) {
				setBranch(branch, true);
			}
		});

		depth.value = clampDepth(scope.value, value.selection_depth);
		expandPath();
	};

	const toFilterValue = (): FilterValueDTO => ({
		id: null,
		model: scope.value?.model ?? null,
		label: scope.value?.label ?? null,
		level: scope.value?.level ?? null,
		selection_depth: depth.value,
		sort_order: null,
		excluded_branches: excludedNodes.value,
	});

	watch(
		root,
		(next, previous) => {
			if (!previous) {
				return;
			}

			clearBranches();
			collapseAll();
			scopeBranch.value = [];
			depth.value = clampDepth(next, 1);
		},
		{ flush: "sync" }
	);

	return {
		root,
		scope,
		scopeBranch,
		breadcrumb,
		depth,
		depthOptions,
		depthYield,
		canGoUp,

		excludedNodes,
		exclusions,
		removeExclusion,
		isBranchExcluded,
		isBranchIndeterminate,
		isBranchPruned,
		canExclude,
		toggleExclusion,
		setExcluded,
		isolate,
		includeAll,
		clearExclusions: clearBranches,

		isExpanded,
		toggleExpanded,
		collapseAll,
		expandPath,

		setScope,
		goUp,
		setDepth,
		isWithin,

		loadFromValue,
		toFilterValue,
	};
};
